/**
 * IT / Telemetry page at /it/telemetry.
 *
 * One place to answer the questions that used to require opening the AWS
 * console: which pages get used, what errors people hit, which routes are slow,
 * and who was active. Each panel is a canned CloudWatch Logs Insights query run
 * server-side by GET /v1/cockpit-ops/telemetry.
 *
 * Two sources feed it. The API access log
 * (/aws/apigateway/api-<stage>-access) is live today and backs the slow-routes,
 * per-user activity, and API-error panels. The browser telemetry log group
 * (/amp/telemetry/<stage>) backs page visits, top pages, and browser errors and
 * does not exist until the collector ships, so those panels show an
 * explanation instead of an error.
 *
 * Insights bills per GB scanned, so a panel only runs when it is the selected
 * one, the time range is explicit (24 hours by default), and nothing polls in
 * the background. Each panel footnote shows how much it scanned.
 *
 * Report, time range, sort, and search all live in the URL, so a refresh or a
 * shared link lands on the same view. Sort and search are client-side over
 * the rows already loaded for the selected report and time range (the pure
 * helpers live in useTelemetry.ts). Clicking a row on API errors opens a
 * detail modal of the individual requests behind that status+route; clicking
 * a name on Activity by user opens that person's full activity feed at
 * /it/telemetry/user.
 *
 * Back link top-left per .claude/rules/cockpit-ui-navigation.md.
 */

import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { gridTheadClassName, InfoTooltip, SortableHeader, type SortDir } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { FilterLabel } from '@/components/FilterLabel';
import { Pill } from '@/components/Pill';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { formatDateTime } from '@/lib/dateUtils';
import {
  columnLabel,
  downloadTelemetryXlsx,
  formatBytes,
  formatCell,
  isNumericColumn,
  matchesSearch,
  sortTelemetryRows,
  useTelemetryReport,
  RANGES,
  DEFAULT_HOURS,
  SOURCE_LABELS,
  type TelemetryReportId,
  type TelemetryRow,
} from '@/lib/useTelemetry';
import { ApiErrorDetailModal } from './ApiErrorDetailModal';

const REPORTS: Array<{ id: TelemetryReportId; label: string; blurb: string }> = [
  {
    id: 'page_visits',
    label: 'Page visits',
    blurb: 'Every browser page view, one row per visit, with who viewed it and from where.',
  },
  {
    id: 'top_pages',
    label: 'Top pages',
    blurb: 'Most-viewed pages across Cockpit, Sweet Spot, and Connect, with how many distinct people viewed each.',
  },
  {
    id: 'errors_by_site',
    label: 'Browser errors',
    blurb: 'JavaScript errors and unhandled promise rejections, grouped by site and message.',
  },
  {
    id: 'slow_routes',
    label: 'Slow routes',
    blurb: 'API routes ranked by 95th-percentile response time, so the slowest 5 percent of requests drive the order.',
  },
  {
    id: 'activity_by_user',
    label: 'Activity by user',
    blurb: 'Signed-in people ranked by request count, with how many distinct pages each touched and when they were last seen.',
  },
  {
    id: 'api_errors',
    label: 'API errors',
    blurb: 'Requests that returned 400 or worse, grouped by status code and route. Click a row for the individual requests.',
  },
];

const DEFAULT_REPORT: TelemetryReportId = 'slow_routes';

// Tint for the `source` column pill (person|otto|device|anonymous). Local to
// this page per COCKPIT.md 2.9: the base shape/size comes from the shared
// Pill component, this only supplies the color layered over it.
const SOURCE_TONES: Record<string, string> = {
  person: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  otto: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  device: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  test: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  anonymous: 'bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted',
};

function isReportId(value: string | null): value is TelemetryReportId {
  return REPORTS.some((r) => r.id === value);
}

function parseSortParam(raw: string | null): { column: string; dir: SortDir } | null {
  if (!raw) return null;
  const [col, dir] = raw.split(':');
  if (!col || !dir) return null;
  if (dir !== 'asc' && dir !== 'desc') return null;
  return { column: col, dir };
}

/** One cell's contents. Most columns render through formatCell; a few carry
 * extra meaning (a colored pill, a tooltip, a link) that formatCell (plain
 * string in, plain string out) cannot express. */
function renderCell(report: TelemetryReportId, column: string, row: TelemetryRow): ReactNode {
  const raw = row[column] ?? '';
  if (column === 'source') {
    const tone = SOURCE_TONES[raw] ?? SOURCE_TONES.anonymous;
    return <Pill tone={tone}>{SOURCE_LABELS[raw] ?? (raw || '—')}</Pill>;
  }
  if (column === 'route') {
    const description = row.route_description;
    return (
      <span className="inline-flex items-baseline gap-1">
        <span>{raw || '—'}</span>
        {description ? <InfoTooltip label="Route">{description}</InfoTooltip> : null}
      </span>
    );
  }
  if (column === 'email' && report === 'activity_by_user' && raw) {
    return (
      <Link
        to={`/it/telemetry/user?email=${encodeURIComponent(raw)}`}
        className="text-amp-olive-deep hover:underline dark:text-amp-olive-dark"
        onClick={(e) => e.stopPropagation()}
      >
        {raw}
      </Link>
    );
  }
  return formatCell(column, raw);
}

/**
 * Mobile card list, replacing the shared shell's default stacked-table
 * fallback. The default splits each row into two equal columns, which on a
 * phone leaves a gulf of dead space between the label and its value (Steven,
 * 2026-08-08). These cards size the label column to its content with a small
 * fixed gap, same approach as Item Sales' cards.
 */
export function TelemetryCards({
  report,
  columns,
  rows,
  notice,
  onRowClick,
}: {
  report: TelemetryReportId;
  columns: string[];
  rows: TelemetryRow[];
  /** Loading / error / empty copy; when set it replaces the card list. */
  notice: ReactNode | null;
  onRowClick?: (row: TelemetryRow) => void;
}) {
  if (notice) {
    return (
      <p className="px-3 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">{notice}</p>
    );
  }
  return (
    <ul className="divide-y divide-amp-line dark:divide-amp-dark-line">
      {rows.map((row, i) => (
        <li
          key={`${i}-${columns.map((c) => row[c] ?? '').join('|')}`}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          className={`px-3 py-2.5 ${
            onRowClick
              ? 'cursor-pointer active:bg-amp-cream-warm dark:active:bg-amp-dark-surface-warm'
              : ''
          }`}
        >
          <dl className="grid grid-cols-[max-content_1fr] items-baseline gap-x-3 gap-y-1">
            {columns.map((c) => (
              <Fragment key={c}>
                <dt className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  {columnLabel(c)}
                </dt>
                <dd className="min-w-0 break-all text-[13px] text-amp-ink dark:text-amp-dark-ink">
                  {renderCell(report, c, row)}
                </dd>
              </Fragment>
            ))}
          </dl>
        </li>
      ))}
    </ul>
  );
}

function SectionSwitcher({
  value,
  onChange,
}: {
  value: TelemetryReportId;
  onChange: (r: TelemetryReportId) => void;
}) {
  return (
    <div>
      <FilterLabel as="div">Report</FilterLabel>
      <div className="flex flex-wrap gap-1.5">
        {REPORTS.map((r) => (
          <Pill
            key={r.id}
            variant={r.id === value ? 'selected' : 'outline'}
            aria-pressed={r.id === value}
            title={r.blurb}
            onClick={() => onChange(r.id)}
          >
            {r.label}
          </Pill>
        ))}
      </div>
    </div>
  );
}

export function Telemetry() {
  const [searchParams, setSearchParams] = useSearchParams();

  const reportParam = searchParams.get('report');
  const report: TelemetryReportId = isReportId(reportParam) ? reportParam : DEFAULT_REPORT;
  const hoursParam = Number(searchParams.get('hours'));
  const hours = RANGES.some((r) => r.hours === hoursParam) ? hoursParam : DEFAULT_HOURS;
  const search = searchParams.get('q') ?? '';
  const sortParam = parseSortParam(searchParams.get('sort'));
  const sortBy = sortParam?.column ?? null;
  const sortDir: SortDir = sortParam?.dir ?? 'asc';

  const updateParams = (mutate: (sp: URLSearchParams) => void) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      },
      { replace: true },
    );
  };

  // Switching the report resets sort/search: a column name that made sense
  // for one report's shape rarely applies to another's.
  const setReport = (v: TelemetryReportId) =>
    updateParams((sp) => {
      sp.set('report', v);
      sp.delete('sort');
      sp.delete('q');
    });
  const setHours = (v: number) => updateParams((sp) => sp.set('hours', String(v)));
  const setSearch = (v: string) => updateParams((sp) => (v ? sp.set('q', v) : sp.delete('q')));
  const toggleSort = (col: string) =>
    updateParams((sp) => {
      if (sortBy !== col) sp.set('sort', `${col}:asc`);
      else if (sortDir === 'asc') sp.set('sort', `${col}:desc`);
      else sp.delete('sort');
    });
  const resetFilters = () =>
    updateParams((sp) => {
      sp.delete('sort');
      sp.delete('q');
      sp.delete('hours');
    });

  const isAnyFilterActive = !!search || !!sortBy || hours !== DEFAULT_HOURS;

  const { data, isLoading, isFetching, error, refetch } = useTelemetryReport(report, hours);
  const active = REPORTS.find((r) => r.id === report);
  const columns = useMemo(() => data?.columns ?? [], [data]);
  const rawRows = useMemo(() => data?.rows ?? [], [data]);
  const searchedRows = useMemo(
    () => rawRows.filter((r) => matchesSearch(r, columns, search)),
    [rawRows, columns, search],
  );
  const rows = useMemo(
    () => sortTelemetryRows(searchedRows, sortBy, sortDir),
    [searchedRows, sortBy, sortDir],
  );

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const onExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadTelemetryXlsx(report, hours);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const [errorDetail, setErrorDetail] = useState<{ status: string; route: string } | null>(null);

  // Shared loading / error / empty copy for the table body and the mobile cards.
  const notice: ReactNode | null =
    rows.length > 0 ? null : isLoading || isFetching ? (
      <span className="inline-flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Running the query. CloudWatch takes a few seconds.
      </span>
    ) : error ? (
      `Could not run this report: ${error.message}`
    ) : (
      (data?.note ?? 'No matching log lines in this window.')
    );

  const onErrorRowClick = (row: TelemetryRow) =>
    setErrorDetail({ status: row.status ?? '', route: row.route ?? '' });

  const toolbar = (
    <div className="flex flex-col gap-3">
      <SectionSwitcher value={report} onChange={setReport} />
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <FilterLabel as="div" className="!mb-0">
            Time range
          </FilterLabel>
          <select
            value={String(hours)}
            onChange={(e) => setHours(Number(e.target.value))}
            className="rounded-md border border-amp-line bg-amp-white px-3 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          >
            {RANGES.map((r) => (
              <option key={r.hours} value={r.hours}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px] flex flex-col gap-1">
          <FilterLabel as="div" className="!mb-0">
            Search
          </FilterLabel>
          <ClearableSearchInput
            value={search}
            onChange={setSearch}
            placeholder="Filter loaded rows..."
            className="w-full border border-amp-line rounded pl-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
          />
        </div>
        {isAnyFilterActive && (
          <button
            type="button"
            onClick={resetFilters}
            className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            Reset filters
          </button>
        )}
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-md border border-amp-line bg-amp-white px-3 py-1.5 text-sm text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Run again
        </button>
        <button
          type="button"
          onClick={() => void onExport()}
          disabled={exporting}
          className="inline-flex items-center gap-1.5 rounded-md border border-amp-line bg-amp-white px-3 py-1.5 text-sm text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm disabled:opacity-50"
        >
          {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Export
        </button>
      </div>
      {active ? (
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted max-w-xl">
          {active.blurb}
        </p>
      ) : null}
    </div>
  );

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
            IT - Telemetry
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">
            Telemetry
          </h1>
          <p className="mt-1 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted max-w-2xl">
            Traffic, errors, latency, and per-user activity read straight from the logs. Each report is a fixed query
            run against a bounded time range, so the answers here are the same ones the AWS console would give.
          </p>
        </div>
        <ToolPinToggle toolSlug="telemetry" toolName="Telemetry" />
      </div>

      {data && !data.available ? (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
          {data.note ?? 'This log source is not available yet.'} Browser pageviews and JavaScript errors start
          appearing here once the telemetry collector is live.
        </div>
      ) : null}

      {exportError ? (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200">
          {exportError}
        </div>
      ) : null}

      <DataGridShell
        title={active?.label ?? 'Report'}
        loaded={rows.length}
        countNoun="rows"
        toolbar={toolbar}
        activeFilterCount={search ? 1 : 0}
        renderCards={() => (
          <TelemetryCards
            report={report}
            columns={columns}
            rows={rows}
            notice={notice}
            onRowClick={report === 'api_errors' ? onErrorRowClick : undefined}
          />
        )}
      >
        <table className="w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              {columns.map((c) => (
                <SortableHeader
                  key={c}
                  label={columnLabel(c)}
                  sorted={sortBy === c ? sortDir : null}
                  onToggle={() => toggleSort(c)}
                  align={isNumericColumn(c) ? 'right' : 'left'}
                  thClassName="px-4 py-2"
                />
              ))}
              {columns.length === 0 ? <th className="px-4 py-2 text-left font-medium">Result</th> : null}
            </tr>
          </thead>
          <tbody>
            {notice ? (
              <tr>
                <td
                  colSpan={Math.max(columns.length, 1)}
                  className="px-4 py-6 text-center text-amp-ink-muted dark:text-amp-dark-ink-muted"
                >
                  {notice}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr
                  key={`${i}-${columns.map((c) => row[c] ?? '').join('|')}`}
                  onClick={report === 'api_errors' ? () => onErrorRowClick(row) : undefined}
                  className={`border-t border-amp-line dark:border-amp-dark-line ${
                    report === 'api_errors'
                      ? 'cursor-pointer hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm'
                      : ''
                  }`}
                >
                  {columns.map((c) => (
                    <td
                      key={c}
                      title={row[c] ?? ''}
                      className={`px-4 py-2 ${
                        isNumericColumn(c)
                          ? 'text-right whitespace-nowrap tabular-nums text-amp-ink dark:text-amp-dark-ink'
                          : 'text-amp-ink dark:text-amp-dark-ink break-all'
                      }`}
                    >
                      {renderCell(report, c, row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>

      {/* pb-8 below md: without it the last line sits flush against the
          bottom of a phone viewport, under the browser chrome (Steven,
          2026-08-08, "the bottom is cutoff"). */}
      {data ? (
        <p className="mt-2 pb-8 text-xs text-amp-ink-muted md:pb-0 dark:text-amp-dark-ink-muted">
          {data.log_group} · scanned {formatBytes(data.bytes_scanned)} · last run {formatDateTime(data.fetched_at)}
        </p>
      ) : null}

      {errorDetail ? (
        <ApiErrorDetailModal
          status={errorDetail.status}
          route={errorDetail.route}
          hours={hours}
          onClose={() => setErrorDetail(null)}
        />
      ) : null}
    </div>
  );
}
